const RESIZE_DEBOUNCE_MS = 150;

/** Question title shrink-to-fit: never scale below this, target this many rows before shrinking. */
export const MIN_QUESTION_FONT_SIZE_PX = 22;
export const QUESTION_TARGET_MAX_ROWS = 3;

/**
 * Shrink-to-fit for the Quiz question title: scale font-size down when the rendered text
 * exceeds a target row count, floored at a minimum legible size. Single-pass — computes
 * the ratio needed to hit the target directly from one overflow measurement rather than
 * an iterative shrink-and-remeasure loop. Ported from ModularMultipleChoice/promptTextScaling.js.
 */
export function computeShrinkToFitFontSizePx({
  measuredHeightPx,
  lineHeightPx,
  fontSizePx,
  targetMaxRows,
  minFontSizePx,
}) {
  const targetMaxHeightPx = targetMaxRows * lineHeightPx;
  if (measuredHeightPx <= targetMaxHeightPx) {
    return null;
  }

  // Height scales roughly with fontSize^2, not fontSize: shrinking the font both
  // shortens each line AND fits more characters per line (in a fixed-width wrapper),
  // so row count itself drops as font-size drops. sqrt(scale) targets the row count
  // directly instead of overshooting into a smaller-than-necessary font.
  const scale = Math.sqrt(targetMaxHeightPx / measuredHeightPx);
  const newFontSizePx = Math.min(fontSizePx, Math.max(minFontSizePx, fontSizePx * scale));
  const newLineHeightPx = lineHeightPx * (newFontSizePx / fontSizePx);

  return { fontSizePx: newFontSizePx, lineHeightPx: newLineHeightPx };
}

/** Reads the base (unscaled) computed font-size/line-height and scrollHeight for a text node. */
function measureBaseTextMetrics(el) {
  el.style.fontSize = '';
  el.style.lineHeight = '';
  const computed = window.getComputedStyle(el);
  return {
    fontSizePx: parseFloat(computed.fontSize),
    lineHeightPx: parseFloat(computed.lineHeight),
    measuredHeightPx: el.scrollHeight,
  };
}

export function applyShrinkToFit(el, { targetMaxRows, minFontSizePx }) {
  if (!el) return;
  const { fontSizePx, lineHeightPx, measuredHeightPx } = measureBaseTextMetrics(el);
  const scaled = computeShrinkToFitFontSizePx({
    measuredHeightPx,
    lineHeightPx,
    fontSizePx,
    targetMaxRows,
    minFontSizePx,
  });
  if (scaled) {
    el.style.fontSize = `${scaled.fontSizePx}px`;
    el.style.lineHeight = `${scaled.lineHeightPx}px`;
  }
}

/**
 * Applies shrink-to-fit to `el` and re-applies on window resize (debounced). Returns a
 * cleanup function. Intended for class-component lifecycle wiring (componentDidMount/Update).
 */
export function watchShrinkToFit(getEl, limits) {
  applyShrinkToFit(getEl(), limits);

  // Re-measure once the reading font finishes loading: an initial measurement taken against
  // the fallback font's metrics (cold session, first question) never otherwise self-corrects.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => applyShrinkToFit(getEl(), limits));
  }

  let debounceTimer = null;
  const onResize = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => applyShrinkToFit(getEl(), limits), RESIZE_DEBOUNCE_MS);
  };
  window.addEventListener('resize', onResize);

  return () => {
    clearTimeout(debounceTimer);
    window.removeEventListener('resize', onResize);
  };
}
